/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    makeAuthenticatedServiceRequest as makeAuthenticatedFetchRequest,
    makePublicServiceRequest as makePublicFetchRequest,
    type FetchResponse,
} from './fetchClient';
import { CancelablePromise } from 'src/types/upload';

/**
 * Makes an authenticated HTTP request using native fetch API
 * Automatically injects authentication token from auth service
 *
 * @param config - Request configuration
 * @param config.url - Target URL
 * @param config.method - HTTP method (default: get)
 * @param config.data - Request body data
 * @param config.header - Additional headers (note: singular 'header' for backward compatibility)
 * @returns Cancelable promise with fetch response
 */
export function makeAuthenticatedServiceRequest<T = any>({
    url,
    method = 'get',
    data,
    header,
}: {
    url: string;
    method?: string;
    data?: any;
    header?: any;
}): CancelablePromise<FetchResponse<T>> {
    return makeAuthenticatedFetchRequest({
        url,
        method: method.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        data,
        headers: header,
    });
}

/**
 * Makes a public (non-authenticated) HTTP request using native fetch API
 *
 * @param config - Request configuration
 * @param config.url - Target URL
 * @param config.method - HTTP method (default: get)
 * @param config.data - Request body data
 * @param config.responseType - Expected response type (json, blob, text)
 * @param config.header - Additional headers (note: singular 'header' for backward compatibility)
 * @returns Cancelable promise with fetch response
 */
export function makePublicServiceRequest<T = any>({
    url,
    method = 'get',
    data,
    responseType,
    header,
}: {
    url: string;
    method?: string;
    data?: any;
    responseType?: 'json' | 'blob' | 'text';
    header?: any;
}): CancelablePromise<FetchResponse<T>> {
    return makePublicFetchRequest({
        url,
        method: method.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        data,
        responseType,
        headers: header,
    });
}
